import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { brendoClient } from '../api/brendoApi';
import { getDatasetKey, parseDatasetSelection } from '../config/resultDatasets';
import PageShell from '../components/layout/PageShell';
import DatasetSelectors from '../components/results/DatasetSelectors';
import ExpressionDialog from '../components/results/ExpressionDialog';
import ResultsTable from '../components/results/ResultsTable';

export default function AllGenesPage({ client = brendoClient }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = useMemo(() => parseDatasetSelection(searchParams), [searchParams]);
  const datasetKey = getDatasetKey(selection);
  const [state, setState] = useState({ status: 'loading', rows: [], error: null });
  const [retryToken, setRetryToken] = useState(0);
  const [selectedGene, setSelectedGene] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', rows: [], error: null });
    client.fetchDataset(datasetKey)
      .then((rows) => {
        if (!cancelled) setState({ status: 'success', rows, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ status: 'error', rows: [], error });
      });
    return () => {
      cancelled = true;
    };
  }, [client, datasetKey, retryToken]);

  const changeSelection = (nextSelection) => {
    setSearchParams({
      method: nextSelection.method,
      direction: nextSelection.direction,
      scope: nextSelection.scope,
    });
  };
  const openExpression = useCallback((id, name) => setSelectedGene({ id, name }), []);

  return (
    <PageShell
      eyebrow="Method-specific exploration"
      title="All genes"
      intro="Choose one statistical method, expression direction, and tissue scope. This view presents the corresponding method-specific results; use Transmembrane Focus for the four-list RRA ranking."
    >
      <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <DatasetSelectors selection={selection} onChange={changeSelection} />
      </Box>

      {state.status === 'loading' && (
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: 10 }}>
          <CircularProgress size={26} />
          <Typography>Loading results…</Typography>
        </Stack>
      )}
      {state.status === 'error' && (
        <Alert
          severity="error"
          action={<Button color="inherit" onClick={() => setRetryToken((value) => value + 1)}>Retry</Button>}
        >
          The selected results could not be loaded.
        </Alert>
      )}
      {state.status === 'success' && !state.rows.length && <Alert severity="info">No genes are available for this result view.</Alert>}
      {state.status === 'success' && state.rows.length > 0 && (
        <ResultsTable
          kind="allGenes"
          rows={state.rows}
          selection={selection}
          onExpression={openExpression}
        />
      )}

      <ExpressionDialog
        open={Boolean(selectedGene)}
        gene={selectedGene}
        onClose={() => setSelectedGene(null)}
        client={client}
      />
    </PageShell>
  );
}
